"use client"

import { Button } from "@/components/ui/button"
import Image from "next/image"
import { Menu, ShoppingCart } from "lucide-react"
import Sphere from "../components/Sphere"
import { Altair } from "@/components/altair/Altair"
import { useLiveAPIContext } from "@/context/LiveAPIContext"
import { useRef, useState } from "react"
import dynamic from 'next/dynamic';
import ClientOnly from '@/components/client-only/ClientOnly';

const ControlTray = dynamic(() => import('@/components/control-tray/ControlTray'), {
  ssr: false,
  loading: () => <div>Loading audio controls...</div>
});

export default function MainPage() {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [videoStream, setVideoStream] = useState<MediaStream | null>(null);
  
   // Get the volume and connected state from LiveAPIContext
   const { connected, volume } = useLiveAPIContext();
  
   // Determine if model is actively speaking (volume-based)
   const isModelSpeaking = volume > 0.01; // Small threshold to avoid noise
   const showDynamicVersion = connected && isModelSpeaking;

  return (
    <div className="min-h-screen bg-background-main text-white flex flex-col">
      {/* Header */}
      <header className="flex items-center justify-between p-6">
        <Button variant="ghost" size="icon" className="text-white hover:bg-slate-700">
          <Menu className="h-6 w-6" />
        </Button>

        <Image
          src="/logoEL.svg"
          alt="Estée Lauder"
          width={150}
          height={20}
          className="h-6 w-auto md:h-8 lg:h-10 object-contain"
          priority
          sizes="(max-width: 768px) 96px, (max-width: 1024px) 120px, 150px"
        />

        <div className="relative">
          <Button variant="ghost" size="icon" className="text-white hover:bg-slate-700">
            <ShoppingCart className="h-6 w-6" />
          </Button>
          <div className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
            1
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex flex-col items-center justify-center px-6 space-y-12 relative">
        {/* Animated Gradient Circle Container */}
        <div 
          className={`relative flex items-center justify-center transition-all duration-1000 ease-in-out ${
            connected 
              ? 'w-[350px] h-[350px] transform translate-y-0' 
              : 'w-48 h-48 md:w-64 md:h-64'
          }`}
        >
       {/* Static Gradient Circle -- Set to a lower z-index */}
  <div 
    className={`absolute inset-0 z-10 transition-all duration-700 ease-in-out opacity-100 scale-100 rotate-0`}
  >
    <div className="relative w-full h-full flex items-center justify-center">
      <div className={`w-40 h-40 rounded-full bg-gradient-to-br from-pink-400 via-purple-400 to-cyan-400 transition-all duration-500 ${
        connected ? 'opacity-90' : 'opacity-70'
      }`}></div>
      <div className={`absolute inset-4 rounded-full bg-gradient-to-br from-pink-300 via-purple-300 to-cyan-300 transition-all duration-500 ${
        connected ? 'opacity-60' : 'opacity-40'
      }`}></div>
      <div className={`absolute inset-8 rounded-full bg-gradient-to-br from-pink-200 via-purple-200 to-cyan-200 transition-all duration-500 ${
        connected ? 'opacity-40' : 'opacity-20'
      }`}></div>
    </div>
  </div>

  {/* Dynamic Sphere Component -- Set to a higher z-index */}
  <div 
    className={`absolute inset-0 z-20 transition-all duration-500 ease-in-out ${
      showDynamicVersion 
        ? 'opacity-100 scale-100 rotate-0' 
        : 'opacity-0 scale-75 rotate-12'
    }`}
  >
    <div className="w-full h-full flex items-center justify-center overflow-visible">
      <div 
        className={`transition-all duration-1000 ease-in-out ${
          connected ? 'w-[350px] h-[350px]' : 'w-40 h-40'
        }`}
      >
        <div className="w-full h-full flex items-center justify-center">
          <Sphere size={160} className="transition-all duration-500" />
        </div>
      </div>
    </div>
  </div>

          {/* Connection Status Indicator */}
          <div 
            className={`absolute -bottom-8 left-1/2 transform -translate-x-1/2 transition-all duration-500 ${
              connected ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
            }`}
          >
            <div className="flex items-center gap-2 bg-blue-600/20 backdrop-blur-sm rounded-full px-3 py-1 border border-blue-400/30">
              <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse"></div>
              <span className="text-xs text-blue-300 font-medium">Connected</span>
            </div>
          </div>
        </div>

        {/* Video Window */}
        <div className="absolute right-[120px] top-[115px]">
          <video
            ref={videoRef}
            className={`
              w-[266px] h-[152px] 
              object-cover 
              rounded-lg 
              border-2 
              border-slate-600 
              shadow-xl
              transition-opacity 
              duration-300
              ${videoStream ? 'opacity-100' : 'opacity-0 pointer-events-none'}
            `}
            autoPlay
            playsInline
            muted
          />
        </div>

        {/* Text and Action Buttons - Fade out when connected */}
        <div 
          className={`text-center space-y-6 transition-all duration-700 ease-in-out ${
            connected 
              ? 'opacity-0 translate-y-8 pointer-events-none hidden' 
              : 'opacity-100 translate-y-0 block'
          }`}
        >
          <h2 className="text-2xl md:text-3xl font-light">
            How can I help you with your<br /> cosmetic needs today?
          </h2>
          
          {/* Action Buttons */}
          <div className="space-y-4">
            {/* First Row */}
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-700 hover:border-slate-500 px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
              >
                Enhance
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-700 hover:border-slate-500 px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
              >
                Glow
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-700 hover:border-slate-500 px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
              >
                Smooth
              </Button>
            </div>

            {/* Second Row */}
            <div className="flex flex-wrap justify-center gap-3">
              <Button
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-700 hover:border-slate-500 px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
              >
                Revive
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-700 hover:border-slate-500 px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
              >
                Cleanse
              </Button>
              <Button
                variant="outline"
                className="bg-transparent border-slate-600 text-white hover:bg-slate-700 hover:border-slate-500 px-6 py-2 rounded-full transition-all duration-300 hover:scale-105"
              >
                Define
              </Button>
            </div>
          </div>
        </div>

        {/* TODO Second Row */}
        {/* <ProductShowcase />*/}
      </main>

      {/* Bottom Section with Enhanced Mic Button */}
      <footer className="flex flex-col items-center pb-8 space-y-4">
        <div className="px-12 space-y-12">
          <Altair />
          <ClientOnly fallback={<div>Loading...</div>}>
            <ControlTray
              videoRef={videoRef}
              supportsVideo={true}
              onVideoStreamChange={setVideoStream}
              enableEditingSettings={false}
            >
              {/* put your own buttons here */}
            </ControlTray>
          </ClientOnly>
        </div>
      </footer>
    </div>
  )
}